import React, { useMemo, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { Modal } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';

import Button from 'components/Button';
import DeleteConfirmModal from 'components/DeleteConfirmModal';
import { ArrowBackLong, Icon } from 'utils/icons';
import { useTagMutation } from 'utils/api/mutations';
import { useTagQuery } from 'utils/api/queries';
import { useHomeScreenContext } from 'layouts/HomeScreenLayout/contexts/HomeScreenContext';
import { formatCurrency, toastStyle } from 'utils/helper';
import {
  removeTagFromBudgetRowCaches,
  updateTagInBudgetCaches,
} from './tagCache';

import './styles.scss';

const emptyForm = {
  name: '',
  color: '#6B7280',
};

const getErrorMessage = error => {
  const data = error?.response?.data;
  if (!data) return error?.message || 'Something went wrong.';
  if (typeof data === 'string') return data;
  if (data.detail) return data.detail;
  if (data.non_field_errors?.[0]) return data.non_field_errors[0];
  const firstKey = Object.keys(data)[0];
  const firstValue = firstKey ? data[firstKey] : null;
  if (Array.isArray(firstValue)) return `${firstKey}: ${firstValue[0]}`;
  if (typeof firstValue === 'string') return `${firstKey}: ${firstValue}`;
  return 'Something went wrong.';
};

const TagsDetails = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { activeBudget, selectedBudget, hasEditAccess } =
    useHomeScreenContext();
  const canEdit = hasEditAccess?.();
  const [form, setForm] = useState(emptyForm);
  const [editingId, setEditingId] = useState(null);
  const [showEditorModal, setShowEditorModal] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);

  const {
    data: tagsResponse,
    isLoading,
    refetch: refetchTags,
  } = useTagQuery.useTagsQuery({
    filters: { active_id: activeBudget },
    enabled: Boolean(activeBudget),
  });

  const tags = useMemo(() => tagsResponse?.data || [], [tagsResponse]);

  const createTagMutation = useTagMutation.useCreateTagMutation();
  const updateTagMutation = useTagMutation.useUpdateTagMutation();
  const deleteTagMutation = useTagMutation.useDeleteTagMutation();
  const isSavingTag =
    createTagMutation.isLoading || updateTagMutation.isLoading;

  const invalidateTags = () => {
    queryClient.invalidateQueries({ queryKey: ['budget-tags'] });
    refetchTags();
  };

  const resetForm = () => {
    setForm(emptyForm);
    setEditingId(null);
  };

  const handleOpenCreateModal = () => {
    resetForm();
    setShowEditorModal(true);
  };

  const handleCloseEditorModal = () => {
    setShowEditorModal(false);
    resetForm();
  };

  const handleSubmit = event => {
    event?.preventDefault();
    if (!canEdit || isSavingTag) return;

    const payload = {
      active_id: activeBudget,
      name: form.name,
      color: form.color,
    };
    const mutation = editingId ? updateTagMutation : createTagMutation;
    const requestPayload = editingId ? { id: editingId, ...payload } : payload;

    mutation.mutate(requestPayload, {
      onSuccess: response => {
        if (editingId) {
          updateTagInBudgetCaches(queryClient, response.data);
        } else {
          toast.success('Tag added.');
        }
        handleCloseEditorModal();
        invalidateTags();
      },
      onError: error => toast.error(getErrorMessage(error), toastStyle),
    });
  };

  const handleEdit = tag => {
    setEditingId(tag.id);
    setForm({
      name: tag.name || '',
      color: tag.color || '#6B7280',
    });
    setShowEditorModal(true);
  };

  const handleDeleteConfirm = () => {
    if (!deleteTarget?.id) return;
    const deletedTagId = deleteTarget.id;
    deleteTagMutation.mutate(deletedTagId, {
      onSuccess: () => {
        removeTagFromBudgetRowCaches(queryClient, deletedTagId);
        toast.success('Tag deleted.');
        setDeleteTarget(null);
        invalidateTags();
      },
      onError: error => toast.error(getErrorMessage(error), toastStyle),
    });
  };

  const pageTitle = `${
    selectedBudget?.full_name || selectedBudget?.name || 'Budget'
  } Tags`;

  return (
    <div className="tags-details-wrapper">
      <div className="tags-details-header">
        <div className="tags-details-heading">
          <button
            type="button"
            className="tags-back-button"
            onClick={() => navigate(-1)}
            aria-label="Go back"
          >
            <ArrowBackLong />
          </button>
          <div>
            <h1>{pageTitle}</h1>
            <p>
              Assign colors to tags and review totals for tagged line items.
            </p>
          </div>
        </div>
        <button
          type="button"
          className="btn btn-primary tags-add-button"
          onClick={handleOpenCreateModal}
          disabled={!canEdit}
        >
          <span>
            <Icon name="CategoryIcon" />
            Add New Tag
          </span>
        </button>
      </div>

      <section className="tags-card">
        <div className="tags-table-wrapper">
          <table className="tags-table">
            <thead>
              <tr>
                <th>Tag</th>
                <th>Items</th>
                <th>Total</th>
                <th aria-label="Actions" />
              </tr>
            </thead>
            <tbody>
              {isLoading && (
                <tr>
                  <td colSpan={4}>Loading tags...</td>
                </tr>
              )}
              {!isLoading &&
                tags.map(tag => (
                  <tr key={tag.id}>
                    <td>
                      <span
                        className="tags-color-swatch"
                        style={{ backgroundColor: tag.color || '#6B7280' }}
                      />
                      <span className="tags-name">{tag.name}</span>
                    </td>
                    <td>{tag.row_count || 0}</td>
                    <td>
                      {formatCurrency(tag.total || 0, tag.total_currency)}
                      {tag.has_missing_exchange_rate && (
                        <span className="tags-rate-warning">*</span>
                      )}
                    </td>
                    <td>
                      <div className="tags-row-actions">
                        <button
                          type="button"
                          onClick={() => handleEdit(tag)}
                          disabled={!canEdit}
                          aria-label={`Edit ${tag.name}`}
                        >
                          <Icon name="PenToSquareIcon" />
                        </button>
                        <button
                          type="button"
                          onClick={() => setDeleteTarget(tag)}
                          disabled={!canEdit}
                          aria-label={`Delete ${tag.name}`}
                        >
                          <Icon name="TrashCanIcon" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              {!isLoading && tags.length === 0 && (
                <tr>
                  <td colSpan={4} className="tags-empty-state">
                    No tags have been added yet.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </section>

      <DeleteConfirmModal
        showModal={Boolean(deleteTarget)}
        handleOnClose={() => setDeleteTarget(null)}
        onCancel={() => setDeleteTarget(null)}
        onConfirm={handleDeleteConfirm}
        confirmText="Are you sure you want to delete"
        itemName={deleteTarget?.name || 'this tag'}
        confirmLabel="Delete"
        disabled={deleteTagMutation.isLoading}
      />

      <Modal
        show={showEditorModal}
        onHide={handleCloseEditorModal}
        centered
        className="tags-editor-modal"
        dialogClassName="tags-editor-dialog"
      >
        <Modal.Body>
          <div className="tags-editor-modal-header">
            <div className="tags-editor-modal-title">
              <Icon name="CategoryIcon" className="tags-editor-modal-icon" />
              <h2>{editingId ? 'Edit Tag' : 'Add New Tag'}</h2>
            </div>
            <button
              type="button"
              className="tags-editor-close-button"
              onClick={handleCloseEditorModal}
              disabled={isSavingTag}
              aria-label="Close"
            >
              <Icon name="CloseIcon" />
            </button>
          </div>
          <form onSubmit={handleSubmit} className="tags-form">
            <div className="tags-form-grid">
              <label>
                <span>Name</span>
                <input
                  value={form.name}
                  onChange={event =>
                    setForm(prev => ({ ...prev, name: event.target.value }))
                  }
                  placeholder="Department"
                  maxLength={40}
                  disabled={!canEdit}
                  required
                />
              </label>
              <label>
                <span>Color</span>
                <input
                  type="color"
                  value={form.color}
                  onChange={event =>
                    setForm(prev => ({ ...prev, color: event.target.value }))
                  }
                  disabled={!canEdit}
                />
              </label>
            </div>
            <div className="tags-form-actions">
              <Button
                type="button"
                label="Cancel"
                className="tags-secondary-button"
                onClick={handleCloseEditorModal}
                isDisabled={isSavingTag}
              />
              <Button
                type="button"
                label={
                  isSavingTag
                    ? editingId
                      ? 'Updating...'
                      : 'Adding...'
                    : editingId
                    ? 'Update Tag'
                    : 'Add Tag'
                }
                className="tags-primary-button"
                onClick={handleSubmit}
                isDisabled={!canEdit || isSavingTag}
              />
            </div>
          </form>
        </Modal.Body>
      </Modal>
    </div>
  );
};

export default TagsDetails;
